import React, { useState, useEffect, useRef, useCallback } from 'react';
import { UTLabel, UTIconButton, AlertHandler } from '@widergy/energy-ui';
import { push } from 'connected-react-router';
import { connect } from 'react-redux';
import { string, bool } from 'prop-types';
import { isEmpty } from '@widergy/web-utils/lib/array';
import Close from '@material-ui/icons/Close';
import { getIn } from 'seamless-immutable';
import { submit } from 'redux-form';

import { primaryPhoneSelector } from 'redux/user/selector';
import { ROUTES } from 'constants/routes';
import { message } from 'utils/stringUtils';
import { useStateWithCallback } from 'utils/hooks';
import { actionCreators as authActions } from 'redux/authHandlers';
import { actionCreators as userActions } from 'redux/user/actions';
import { actionCreators as utilityActions } from 'redux/utility/actions';
import { formsOptionsType, officesType, officeAvailabilityDaysType, officeSlotType } from 'types/turnsTypes';
import { TURN_USER_INFORMATION } from 'constants/reduxForms';

import ModalPresentationRequirements from './components/ModalPresentationRequirements';
import SelectForm from './components/SelectForm';
import Offices from './components/Offices';
import Calendar from './components/Calendar';
import OfficeSlot from './components/OfficeSlot';
import UserInformation from './components/UserInformation';
import CreateNewTurn from './components/CreateNewTurn';
import styles from './styles.module.scss';
import { TIME_SCROLL_BOTTOM, REF_FORMS, REF_OFFICE, REF_CALENDAR, REF_SLOT, COMMERCIAL } from './constants';
import { initialMonth, initialYear, compareUserInformation } from './utils';

const NewTurnContainer = ({
  dispatch,
  offices,
  officeAvailabilityDays,
  officeAvailabilityDaysLoading,
  officeSlot,
  turnId,
  formsOptions,
  presentationRequirementsURL,
  turnSubmitError,
  turnSubmitLoading,
  phone,
  email,
  clientNumber,
  isCustomer
}) => {
  const handleScrollBottom = ref => ref.current && ref.current.scrollIntoView({ behavior: 'smooth' });
  const refCalendar = useRef(null);

  const handleOfficeChange = useCallback(currentOffice => {
    if (currentOffice) {
      dispatch(utilityActions.getOfficeAvailabilityDays(currentOffice, initialMonth(), initialYear()));
      setTimeout(() => handleScrollBottom(refCalendar), TIME_SCROLL_BOTTOM);
    }
  }, []);

  const refForms = useRef(null);
  const refOffices = useRef(null);
  const refOfficeSlot = useRef(null);
  const refUserInformation = useRef(null);
  const [submitTurn, setSubmitTurn] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [currentForm, setCurrentForm] = useState(null);
  const [currentOffice, setCurrentOffice] = useStateWithCallback(null, handleOfficeChange);
  const [currentDay, setCurrentDay] = useState(null);
  const [currentOfficeSlot, setCurrentOfficeSlot] = useState(null);
  const [currentDate, setCurrentDate] = useState(null);
  const [userInformation, setUserInformation] = useState({});

  const handleGetOffices = () => dispatch(utilityActions.getOffices(COMMERCIAL, true));

  useEffect(() => {
    if (!turnSubmitLoading && submitTurn) {
      if (turnSubmitError) {
        dispatch(push(ROUTES.MY_TURNS));
      } else setOpenModal(true);
    }
  }, [turnSubmitLoading, submitTurn]);

  useEffect(() => {
    if (isEmpty(formsOptions)) {
      dispatch(push(ROUTES.MY_TURNS));
      AlertHandler.error(message('DefaultMessages:getFormsFailure'));
    }
    if (isCustomer !== false) dispatch(authActions.getUserProfile(false));
    handleGetOffices();
    return () => {
      dispatch(userActions.cleanTurnData());
      dispatch(utilityActions.cleanOfficeData());
    };
  }, []);

  const handleGetOfficeSlot = () => {
    const daySelected = officeAvailabilityDays.find(item => item.day === currentDay.getDate());
    if (daySelected && daySelected.available_turns) {
      dispatch(utilityActions.getOfficeSlot(currentOffice, daySelected.id));
    } else dispatch(utilityActions.cleanOfficeSlot());
  };

  useEffect(() => {
    if (!officeAvailabilityDaysLoading) setCurrentDay(currentDate);
  }, [officeAvailabilityDays]);

  useEffect(() => {
    if (currentDay) handleGetOfficeSlot();
  }, [currentDay]);

  const handleGetDays = () => {
    setCurrentDate(new Date());
    dispatch(utilityActions.getOfficeAvailabilityDays(currentOffice, initialMonth(), initialYear()));
  };

  const handleSetForm = option => {
    const optionSelected = option && option.target && option.target.value;
    const formSelected = formsOptions.find(_option => _option.value === optionSelected);
    setCurrentForm(formSelected);
  };

  const handleSetOffice = office => {
    if (office !== currentOffice) {
      setCurrentOffice(office);
      setCurrentDay(null);
      setCurrentOfficeSlot(null);
      setCurrentDate(new Date());
    }
  };

  const handleChangeMonth = date => {
    setCurrentDate(date);
    setCurrentDay(null);
    setCurrentOfficeSlot(null);
    dispatch(
      utilityActions.getOfficeAvailabilityDays(currentOffice, date.getMonth() + 1, date.getFullYear())
    );
  };

  const handleSetDay = date => {
    setCurrentDay(null);
    setCurrentDay(date);
    setCurrentOfficeSlot(null);
    setTimeout(() => handleScrollBottom(refOfficeSlot), TIME_SCROLL_BOTTOM);
  };

  const handleSetOfficeSlot = slote => {
    setCurrentOfficeSlot(slote);
    setTimeout(() => handleScrollBottom(refUserInformation), TIME_SCROLL_BOTTOM);
  };

  const handleGoOption = ref => {
    const constantsRefs = {
      [REF_FORMS]: refForms,
      [REF_OFFICE]: refOffices,
      [REF_CALENDAR]: refCalendar,
      [REF_SLOT]: refOfficeSlot
    };
    handleScrollBottom(constantsRefs[ref]);
  };

  const handleConfirmateTurn = ({ fieldPhone, fieldEmail, fieldClientNumber }) => {
    setUserInformation({ phone: fieldPhone, email: fieldEmail, clientNumber: fieldClientNumber });
    if (turnId)
      dispatch(
        userActions.updateTurn(
          turnId,
          currentOfficeSlot,
          currentForm.label,
          fieldPhone,
          fieldEmail,
          fieldClientNumber
        )
      );
    else
      dispatch(
        userActions.createNewTurn(
          currentOfficeSlot,
          currentForm.label,
          fieldPhone,
          fieldEmail,
          fieldClientNumber
        )
      );
    setSubmitTurn(true);
  };

  const handleSubmitForm = () => dispatch(submit(TURN_USER_INFORMATION));

  const handleCloseModal = () => {
    setOpenModal(false);
    if (
      compareUserInformation(phone, email, userInformation.phone, userInformation.email) &&
      isCustomer !== false
    ) {
      dispatch(push(ROUTES.CHANGE_USER_INFO));
      AlertHandler.info(message('Turns:updateUserInformation'));
    } else dispatch(push(ROUTES.MY_TURNS));
  };

  const handleCancelNewTurn = () => dispatch(push(ROUTES.MY_TURNS));

  return (
    <div>
      <div className={styles.titleContainer}>
        <UTLabel large bold className={styles.title}>
          {message('Turns:newTurnTitle')}
        </UTLabel>
        <UTIconButton onClick={handleCancelNewTurn}>
          <Close />
        </UTIconButton>
      </div>
      <SelectForm
        refForms={refForms}
        currentForm={currentForm}
        changeCurrentForm={handleSetForm}
        options={formsOptions}
      />
      <Offices
        refOffices={refOffices}
        currentForm={!!currentForm}
        currentOffice={currentOffice}
        handleSetOffice={handleSetOffice}
        reload={handleGetOffices}
      />
      <Calendar
        currentOffice={currentOffice}
        currentDay={currentDay}
        handleChangeMonth={handleChangeMonth}
        handleSetDay={handleSetDay}
        reload={handleGetDays}
        refCalendar={refCalendar}
      />
      <OfficeSlot
        refOfficeSlot={refOfficeSlot}
        currentOfficeSlot={currentOfficeSlot}
        currentDay={currentDay}
        handleSetOfficeSlot={handleSetOfficeSlot}
        reload={handleGetOfficeSlot}
      />
      <UserInformation
        refUserInformation={refUserInformation}
        currentOfficeSlot={currentOfficeSlot}
        handleSubmit={handleConfirmateTurn}
        phone={phone}
        email={email}
        clientNumber={clientNumber}
      />
      {currentForm && currentOfficeSlot && currentOffice && currentDay && (
        <CreateNewTurn
          form={currentForm}
          office={offices.find(item => item.id === currentOffice)}
          date={currentDay}
          slot={officeSlot.find(item => item.id === currentOfficeSlot)}
          handleGoOption={handleGoOption}
          handleConfirmateTurn={handleSubmitForm}
          loading={turnSubmitLoading}
        />
      )}
      <ModalPresentationRequirements
        isOpen={openModal}
        closeModal={handleCloseModal}
        alertFormOV={currentForm && currentForm.form_ov}
        presentationRequirementsURL={presentationRequirementsURL}
      />
    </div>
  );
};

NewTurnContainer.propTypes = {
  formsOptions: formsOptionsType,
  offices: officesType,
  officeAvailabilityDays: officeAvailabilityDaysType,
  officeAvailabilityDaysLoading: bool,
  officeSlot: officeSlotType,
  turnId: string,
  presentationRequirementsURL: string,
  turnSubmitError: string,
  turnSubmitLoading: bool,
  isCustomer: bool,
  phone: string,
  email: string,
  clientNumber: string
};

const mapStateToProps = store => ({
  presentationRequirementsURL: getIn(
    store,
    ['utility', 'appConfiguration', 'turns_procedure_requirements_url'],
    ''
  ),
  formsOptions: getIn(store, ['utility', 'appConfiguration', 'turn_forms'], []),
  offices: getIn(store, ['utility', 'commercialOffices', 'offices'], []),
  officeAvailabilityDays: store.utility.officeAvailabilityDays,
  officeAvailabilityDaysLoading: store.utility.officeAvailabilityDaysLoading,
  officeAvailabilityDaysError: store.utility.officeAvailabilityDaysError,
  officeSlot: store.utility.officeSlot,
  turnId: store.user.turnId,
  turnSubmitError: store.user.createNewTurnError || store.user.updateTurnError,
  turnSubmitLoading: store.user.createNewTurnLoading || store.user.updateTurnLoading,
  phone: primaryPhoneSelector(store),
  email: getIn(store.auth, ['currentUser', 'email']),
  isCustomer: getIn(store, ['auth', 'currentUser', 'is_customer']),
  clientNumber: getIn(store.account, ['currentAccount', 'client_number'])
});

export default connect(mapStateToProps)(NewTurnContainer);
